import { useState, useEffect } from 'react';
import { Wrench, ChevronDown, ChevronUp, Check, Loader2 } from 'lucide-react';
import { equipmentService } from '../../services/equipment';
import type { Equipment } from '../../types/equipment';
import { useOrgTimezone, formatLocalDateInTz } from '@/utils/timezone';

interface CalibrationForm {
  tank_capacity_litres: string;
  nozzle_type: string;
  nozzle_spacing_cm: string;
  boom_width_m: string;
  operating_pressure_bar: string;
  operating_speed_kph: string;
  output_rate_lpm: string;
  output_rate_lpha: string;
  calibration_date: string;
  calibration_notes: string;
}

function equipmentToForm(eq: Equipment): CalibrationForm {
  return {
    tank_capacity_litres:   eq.tank_capacity_litres   != null ? String(eq.tank_capacity_litres)   : '',
    nozzle_type:            eq.nozzle_type             ?? '',
    nozzle_spacing_cm:      eq.nozzle_spacing_cm       != null ? String(eq.nozzle_spacing_cm)       : '',
    boom_width_m:           eq.boom_width_m             != null ? String(eq.boom_width_m)             : '',
    operating_pressure_bar: eq.operating_pressure_bar  != null ? String(eq.operating_pressure_bar)  : '',
    operating_speed_kph:    eq.operating_speed_kph     != null ? String(eq.operating_speed_kph)     : '',
    output_rate_lpm:        eq.output_rate_lpm         != null ? String(eq.output_rate_lpm)         : '',
    output_rate_lpha:       eq.output_rate_lpha        != null ? String(eq.output_rate_lpha)        : '',
    calibration_date:       eq.calibration_date        ? eq.calibration_date.slice(0, 10)           : new Date().toISOString().slice(0, 10),
    calibration_notes:      eq.calibration_notes       ?? '',
  };
}

interface EquipmentCalibrationPanelProps {
  /** The currently selected equipment (full object from active list) */
  equipment: Equipment;
  /** Called with the updated equipment object after a successful save */
  onCalibrationSaved?: (updated: Equipment) => void;
}

/**
 * Collapsible calibration panel shown inside the SprayCreate schedule step.
 *
 * - Pre-fills from the equipment's stored calibration data
 * - Lets operators update nozzle, pressure, output rates for this specific spray
 * - Saves directly to the equipment record via PUT /api/equipment/:id
 * - Collapses by default; expands with a single click
 */
function EquipmentCalibrationPanel({ equipment, onCalibrationSaved }: EquipmentCalibrationPanelProps) {
  const orgTz = useOrgTimezone();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<CalibrationForm>(() => equipmentToForm(equipment));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Re-initialise if a different piece of equipment is selected
  useEffect(() => {
    setForm(equipmentToForm(equipment));
    setSaved(false);
    setSaveError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipment.id]);

  const set = (field: keyof CalibrationForm) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm(f => ({ ...f, [field]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      const payload: Record<string, unknown> = {
        calibration_date: form.calibration_date || new Date().toISOString().slice(0, 10),
      };
      const numFields: (keyof CalibrationForm)[] = [
        'tank_capacity_litres','nozzle_spacing_cm','boom_width_m',
        'operating_pressure_bar','operating_speed_kph','output_rate_lpm','output_rate_lpha',
      ];
      for (const k of numFields) {
        const v = parseFloat(form[k]);
        payload[k] = isNaN(v) ? null : v;
      }
      if (form.nozzle_type)       payload.nozzle_type       = form.nozzle_type;
      if (form.calibration_notes) payload.calibration_notes = form.calibration_notes;

      const res = await equipmentService.update(equipment.id, payload as any);
      if (res.success && res.data) {
        setSaved(true);
        onCalibrationSaved?.(res.data);
        setTimeout(() => setSaved(false), 3000);
      } else {
        setSaveError((res as any).error?.message || 'Failed to save calibration');
      }
    } catch (e: any) {
      setSaveError(e?.message || 'Unexpected error');
    } finally {
      setSaving(false);
    }
  };

  const hasExistingCalibration = !!(
    equipment.tank_capacity_litres || equipment.nozzle_type || equipment.output_rate_lpha ||
    equipment.output_rate_lpm || equipment.calibration_date
  );

  return (
    <div className="border border-neutral-200 rounded-lg overflow-hidden">
      {/* Header toggle */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-neutral-50 hover:bg-neutral-100 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <Wrench className="h-4 w-4 text-amber-600" />
          <span className="text-sm font-medium text-neutral-700">
            Calibrate: {equipment.name}
          </span>
          {hasExistingCalibration && !open && (
            <span className="text-xs text-neutral-400">
              {equipment.calibration_date
                ? `last calibrated ${formatLocalDateInTz(equipment.calibration_date, orgTz)}`
                : equipment.output_rate_lpha
                ? `${equipment.output_rate_lpha} L/ha`
                : 'data on file'}
            </span>
          )}
          {saved && (
            <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
              <Check className="h-3.5 w-3.5" /> Saved
            </span>
          )}
        </div>
        {open
          ? <ChevronUp className="h-4 w-4 text-neutral-400" />
          : <ChevronDown className="h-4 w-4 text-neutral-400" />
        }
      </button>

      {/* Expanded form */}
      {open && (
        <div className="px-4 py-4 space-y-4 bg-white">
          <p className="text-xs text-neutral-500">
            Update this equipment's calibration settings. Changes are saved to the equipment record
            and timestamped so you have a history of each calibration event.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {/* Row 1 — tank + nozzle type + nozzle spacing */}
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1">Tank Capacity (L)</label>
              <input type="number" value={form.tank_capacity_litres} onChange={set('tank_capacity_litres')}
                min={0} step="1" placeholder="e.g. 1200"
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1">Nozzle Type</label>
              <input type="text" value={form.nozzle_type} onChange={set('nozzle_type')}
                placeholder="e.g. Albuz ATR Blue"
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1">Nozzle Spacing (cm)</label>
              <input type="number" value={form.nozzle_spacing_cm} onChange={set('nozzle_spacing_cm')}
                min={0} step="0.1"
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>

            {/* Row 2 — boom + pressure + speed */}
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1">Boom Width (m)</label>
              <input type="number" value={form.boom_width_m} onChange={set('boom_width_m')}
                min={0} step="0.1"
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1">Pressure (bar)</label>
              <input type="number" value={form.operating_pressure_bar} onChange={set('operating_pressure_bar')}
                min={0} step="0.1"
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1">Speed (kph)</label>
              <input type="number" value={form.operating_speed_kph} onChange={set('operating_speed_kph')}
                min={0} step="0.1"
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>

            {/* Row 3 — output rates + calibration date */}
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1">Output (L/min)</label>
              <input type="number" value={form.output_rate_lpm} onChange={set('output_rate_lpm')}
                min={0} step="0.001"
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1">Output (L/ha)</label>
              <input type="number" value={form.output_rate_lpha} onChange={set('output_rate_lpha')}
                min={0} step="0.001"
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1">Calibration Date</label>
              <input type="date" value={form.calibration_date} onChange={set('calibration_date')}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-neutral-500 mb-1">Calibration Notes</label>
            <textarea value={form.calibration_notes} onChange={set('calibration_notes')} rows={2}
              placeholder="e.g. Nozzles replaced, re-zeroed output at 8 bar"
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none" />
          </div>

          {saveError && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{saveError}</p>
          )}

          <div className="flex items-center justify-between pt-1">
            <p className="text-xs text-neutral-400">
              Saves to equipment record — visible in equipment history
            </p>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-60"
            >
              {saving
                ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…</>
                : saved
                ? <><Check className="h-3.5 w-3.5" /> Saved</>
                : <><Wrench className="h-3.5 w-3.5" /> Save Calibration</>
              }
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default EquipmentCalibrationPanel;
